import { useState, useEffect, useCallback } from "react";
import { getAxiosInstance } from "../utils/apiClient";
import { getTokens } from "../utils/tokens";
import { useAppSelector } from "../redux/typedHooks";
import { UserInterface } from "../types/user";

interface FreeMessagesStatus {
  isViewer: boolean;
  freeMessagesUsed: number;
  freeMessagesRemaining: number;
  freeMessagesLimit: number;
  lastReset: string;
  nextReset: string;
}

interface FreeMessagesResponse {
  success: boolean;
  data: FreeMessagesStatus;
  message?: string;
}

export const useFreeMessagesStatus = () => {
  const [status, setStatus] = useState<FreeMessagesStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );

  const fetchFreeMessagesStatus = useCallback(async () => {
    if (!user || user.userType?.title !== "viewer") {
      setStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tokens = getTokens();
      const response = await getAxiosInstance().get<FreeMessagesResponse>(
        "/api/free-messages/status",
        {
          headers: {
            "Content-Type": "application/json",
            [process.env
              .REACT_APP_AUTH_TOKEN as any]: `Bearer ${tokens.access_token}`,
            [process.env
              .REACT_APP_FIREBASE_AUTH_TOKEN as any]: `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        }
      );

      if (response.data.success) {
        setStatus(response.data.data);
      } else {
        setError(
          response.data.message || "Failed to fetch free messages status"
        );
      }
    } catch (err) {
      console.error("Error fetching free messages status:", err);
      const errorMessage =
        (err as any)?.response?.data?.message ||
        "Failed to fetch free messages status";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refetch = useCallback(() => {
    fetchFreeMessagesStatus();
  }, [fetchFreeMessagesStatus]);

  useEffect(() => {
    fetchFreeMessagesStatus();

    // Refresh status every 2 minutes
    const interval = setInterval(fetchFreeMessagesStatus, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchFreeMessagesStatus]);

  return {
    status,
    loading,
    error,
    refetch,
    isViewer: status?.isViewer || false,
    freeMessagesRemaining: status?.freeMessagesRemaining || 0,
    freeMessagesUsed: status?.freeMessagesUsed || 0,
    freeMessagesLimit: status?.freeMessagesLimit || 2,
    hasFreeMessages: (status?.freeMessagesRemaining || 0) > 0,
    isFreeQuotaExhausted: (status?.freeMessagesRemaining || 0) === 0,
    nextReset: status?.nextReset || null,
  };
};

export default useFreeMessagesStatus;
